import React from 'react';
import { motion, usePresence, useMotionValue, animate } from 'framer-motion';

interface BoardBottomDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onOpen?: () => void;
    children: React.ReactNode;
    height?: string | number;
    initialHeight?: string | number;
    className?: string;
    showOverlay?: boolean;
    showTints?: boolean;
    position?: 'absolute' | 'fixed';
    zIndex?: number;
}

export const BoardBottomDrawer = React.memo(({
    isOpen,
    onClose,
    onOpen,
    children,
    height = '45vh',
    initialHeight = '84px',
    className = '',
    showOverlay = true,
    showTints = true,
    position = 'absolute',
    zIndex = 30
}: BoardBottomDrawerProps) => {
    const [isPresent] = usePresence();
    const isActuallyOpen = isOpen && isPresent;

    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const touchStartY = React.useRef(0);
    const canCloseOnTouchRef = React.useRef(false);
    
    const wheelBlockedRef = React.useRef(false);
    const wheelAccumulator = React.useRef(0);
    const wheelTimeoutRef = React.useRef<any>(null);
    const yOffset = useMotionValue(0);
    const currentTouchOffset = React.useRef(0);

    const resolvedHeight = typeof height === 'number' ? `${height}px` : height;
    const computedMaxHeight = `min(${resolvedHeight}, calc(100% - 80px))`;

    // Handle initial scroll/drag to open when collapsed
    const handleScrollAttempt = (e: React.WheelEvent | React.TouchEvent) => {
        if (isOpen || !onOpen) return;

        if ('deltaY' in e) {
            // Wheel event: deltaY > 0 usually means scrolling down
            if (e.deltaY > 0) {
                onOpen();
            }
        } else if (e.type === 'touchmove') {
            // Touch move - for simplicity, any move when collapsed opens it
            onOpen();
        }
    };

    const handleContentWheel = (e: React.WheelEvent) => {
        if (!isOpen) return;

        if (wheelTimeoutRef.current) clearTimeout(wheelTimeoutRef.current);

        if (scrollContainerRef.current) {
            // Actively scrolling down content -> Block closing entirely
            if (scrollContainerRef.current.scrollTop > 0) {
                wheelBlockedRef.current = true;
                wheelAccumulator.current = 0;
            } else if (!wheelBlockedRef.current) {
                // Must have started sequence from scrollTop 0 without being previously blocked
                if (e.deltaY < 0) {
                    wheelAccumulator.current += Math.abs(e.deltaY);
                    yOffset.set(wheelAccumulator.current * 0.4); // Visual resistance

                    if (wheelAccumulator.current > 120) {
                        onClose();
                        animate(yOffset, 0, { type: 'spring', damping: 30, stiffness: 400, mass: 0.8 });
                        wheelAccumulator.current = 0;
                    }
                } else {
                    wheelAccumulator.current = 0;
                    animate(yOffset, 0, { type: 'spring', damping: 30, stiffness: 400, mass: 0.8 });
                }
            } else {
                wheelAccumulator.current = 0;
            }
        }

        // Must stop scrolling for 300ms to allow a "second" scroll to trigger close
        wheelTimeoutRef.current = setTimeout(() => {
            wheelBlockedRef.current = false;
            if (wheelAccumulator.current > 0) {
                animate(yOffset, 0, { type: 'spring', damping: 30, stiffness: 400, mass: 0.8 });
                wheelAccumulator.current = 0;
            }
        }, 300);
    };

    const handleContentTouchStart = (e: React.TouchEvent) => {
        touchStartY.current = e.touches[0].clientY;
        currentTouchOffset.current = 0;
        // Only allow touch-to-close if we explicitly start the touch while already at the very top.
        if (scrollContainerRef.current && scrollContainerRef.current.scrollTop <= 0) {
            canCloseOnTouchRef.current = true;
        } else {
            canCloseOnTouchRef.current = false;
        }
    };

    const handleContentTouchMove = (e: React.TouchEvent) => {
        if (!isOpen || !canCloseOnTouchRef.current) return;
        
        if (scrollContainerRef.current && scrollContainerRef.current.scrollTop <= 0) {
            const touchY = e.touches[0].clientY;
            const offset = touchY - touchStartY.current;
            
            if (offset > 0) {
                const frictionOffset = offset * 0.6; // give it a heavier "pull" feel
                currentTouchOffset.current = frictionOffset;
                yOffset.set(frictionOffset);
            } else {
                currentTouchOffset.current = 0;
                yOffset.set(0);
            }
        }
    };

    const handleContentTouchEnd = () => {
        if (!isOpen || !canCloseOnTouchRef.current) return;
        
        if (currentTouchOffset.current > 80) { // closing threshold
            onClose();
        }
        
        animate(yOffset, 0, { type: 'spring', damping: 30, stiffness: 400, mass: 0.8 });
        
        canCloseOnTouchRef.current = false;
        currentTouchOffset.current = 0;
    };

    return (
        <div className="contents">
            {/* Overlay - Decoupled with usePresence for instant disappearance */}
            <motion.div
                initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
                animate={{
                    opacity: (isActuallyOpen && showOverlay) ? 1 : 0,
                    backdropFilter: (isActuallyOpen && showOverlay) ? 'blur(4px)' : 'blur(0px)'
                }}
                transition={{ duration: 0.2 }}
                onClick={onClose}
                style={{ zIndex: zIndex - 5 }}
                className={`${position} inset-0 bg-black/20 drawer-backdrop ${(isActuallyOpen && showOverlay) ? 'pointer-events-auto' : 'pointer-events-none'} ${className}`}
            />

            {/* Bottom Drawer */}
            <motion.div
                animate={{
                    height: isOpen ? 'auto' : initialHeight,
                    opacity: 1
                }}
                initial={{ opacity: 0 }}
                exit={{
                    opacity: 0,
                    transition: { type: 'spring', damping: 30, stiffness: 450, mass: 0.7 }
                }}
                transition={{
                    height: {
                        type: 'spring', damping: 30, stiffness: 400, mass: 0.8
                    },
                    opacity: { duration: 0.2 }
                }}
                onWheel={handleScrollAttempt}
                onTouchMove={handleScrollAttempt}
                style={{ zIndex, maxHeight: computedMaxHeight, y: yOffset }}
                className={`${position} bottom-0 left-0 right-0 bg-white dark:bg-neutral-900 border-x border-t rounded-t-lg shadow-[0_-10px_40px_-15px_rgba(0,0,0,0.2)] overflow-hidden flex flex-col will-change-[height,transform] ${!isOpen ? 'border-accent-primary/10 dark:border-accent-primary/20' : 'border-neutral-200 dark:border-neutral-800'} ${className}`}
                layoutRoot
            >
                {/* Background Tint & Icon (whenever showTints is true) */}
                {showTints && (
                    <>
                        <div className="absolute inset-0 bg-accent-primary/5 dark:bg-accent-primary/10 pointer-events-none" />
                        <div className="absolute top-0 right-0 p-6 opacity-5 pointer-events-none">
                            <span className="w-24 h-24 text-accent-primary" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_51112e7d.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_51112e7d.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                        </div>
                    </>
                )}

                <div className="relative flex flex-col flex-1 min-h-0 overflow-hidden">
                    {/* Handle */}
                    <div 
                        className="pt-3 pb-2 cursor-pointer w-full flex justify-center shrink-0"
                        onClick={() => isOpen ? onClose() : onOpen?.()}
                    >
                        <div className="h-1 w-10 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
                    </div>

                    {/* Content */}
                    <div 
                        ref={scrollContainerRef}
                        onWheel={handleContentWheel}
                        onTouchStart={handleContentTouchStart}
                        onTouchMove={handleContentTouchMove}
                        onTouchEnd={handleContentTouchEnd}
                        onTouchCancel={handleContentTouchEnd}
                        className={`flex-1 flex flex-col ${isOpen ? 'overflow-y-auto' : 'overflow-hidden'} scrollbar-hide px-4 pb-4 pt-1`}
                    >
                        {children}
                    </div>
                </div>
            </motion.div>
        </div>
    );
});
